import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useCRMCall, CallState, CallType } from '@/features/crm/model/useCRMCall';
import { ChatContact } from '@/entities/chat/model/types';
import { useAuth } from '@/shared/model/authContext';
import dynamic from '@/shared/lib/router/dynamic';

// O provider vive no _app (todas as paginas); a UI da chamada so e necessaria durante uma chamada,
// entao fica fora do bundle inicial. O modal continua montado (preserva o estado entre chamadas).
const CRMChatCallModal = dynamic(() => import('@/features/crm/components/chat/CRMChatCallModal').then((m) => m.CRMChatCallModal), { ssr: false });
const CRMChatCallFloating = dynamic(() => import('@/features/crm/components/chat/CRMChatCallFloating').then((m) => m.CRMChatCallFloating), { ssr: false });

interface CRMCallContextData {
  callState: CallState;
  callType: CallType;
  incomingCall: { senderId: string; type: CallType } | null;
  remoteStreams: Map<string, MediaStream>;
  localStream: MediaStream | null;
  isMuted: boolean;
  isCameraOff: boolean;
  isScreenSharing: boolean;
  devices: { audioIn: MediaDeviceInfo[], videoIn: MediaDeviceInfo[], audioOut: MediaDeviceInfo[] };
  selectedDevices: { audioIn: string, videoIn: string, audioOut: string };
  activeContact: ChatContact | null;
  isCallMinimized: boolean;
  startCall: (contact: ChatContact, type: CallType) => Promise<void>;
  answerCall: () => Promise<void>;
  rejectCall: () => void;
  endCall: () => void;
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => Promise<void>;
  sendSoundEffect: (url: string) => void;
  sendServerMute: (targetId: string, mute: boolean) => void;
  switchDevice: (kind: 'audioIn' | 'videoIn', deviceId: string) => Promise<void>;
  minimizeCall: () => void;
  maximizeCall: () => void;
}

const CRMCallContext = createContext<CRMCallContextData>({} as CRMCallContextData);

export const CRMCallProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userProfile } = useAuth();
  const [activeContact, setActiveContact] = useState<ChatContact | null>(null);
  const [isCallMinimized, setIsCallMinimized] = useState(false);

  const {
    callState, callType, incomingCall, remoteStreams, localStream,
    isMuted, isCameraOff, isScreenSharing, devices, selectedDevices,
    startCall: originalStartCall, answerCall, rejectCall, endCall,
    toggleMute, toggleCamera, toggleScreenShare, sendSoundEffect, sendServerMute, switchDevice
  } = useCRMCall(userProfile?.profile.id || '', activeContact);

  // Auto-Rejoin em caso de Reload
  useEffect(() => {
    if (!userProfile?.profile.id) return;
    
    const saved = sessionStorage.getItem('crm_active_call');
    if (saved) {
        try {
            const { topicId, type, timestamp } = JSON.parse(saved);
            if (Date.now() - timestamp < 60000) {
                const fallbackContact: ChatContact = { id: topicId, name: 'Recuperando...', status: 'online', role: 'Colaborador' };
                setActiveContact(fallbackContact);
                originalStartCall(fallbackContact, type, true);
            }
        } catch (e) { sessionStorage.removeItem('crm_active_call'); }
    }
  }, [userProfile?.profile.id]);

  const startCall = useCallback(async (contact: ChatContact, type: CallType) => {
    setActiveContact(contact);
    setIsCallMinimized(false);
    await originalStartCall(contact, type);
  }, [originalStartCall]);

  const minimizeCall = useCallback(() => setIsCallMinimized(true), []);
  const maximizeCall = useCallback(() => setIsCallMinimized(false), []);

  const handlePopout = useCallback(() => {
    if (activeContact) {
      const participantId = activeContact.members?.find(m => m.id !== userProfile?.profile.id)?.id || activeContact.id;
      const url = `/crm/call/${activeContact.id}?type=${callType}&participantId=${participantId}`;
      window.open(url, '_blank', 'width=1280,height=720,menubar=no,toolbar=no,location=no,status=no');
      endCall();
    }
  }, [activeContact, callType, endCall, userProfile]);

  return (
    <CRMCallContext.Provider value={{
      callState, callType, incomingCall, remoteStreams, localStream,
      isMuted, isCameraOff, isScreenSharing, devices, selectedDevices,
      activeContact, isCallMinimized,
      startCall, answerCall, rejectCall, endCall,
      toggleMute, toggleCamera, toggleScreenShare, sendSoundEffect, sendServerMute, switchDevice,
      minimizeCall, maximizeCall
    }}>
      {children}
      
      {/* Só monta (e baixa o chunk do modal) quando há chamada: fora do caminho crítico do boot. */}
      {callState !== 'IDLE' && (
        <CRMChatCallModal
          isOpen={!isCallMinimized}
          onClose={() => {
            // Não minimizar/fechar durante chamada ativa — previne desconexão acidental
            if (callState === 'RINGING') {
              minimizeCall();
            }
          }}
          callState={callState}
          callType={callType}
          contact={activeContact || undefined}
          localStream={localStream}
          remoteStreams={remoteStreams}
          isMuted={isMuted}
          isCameraOff={isCameraOff}
          isScreenSharing={isScreenSharing}
          devices={devices}
          selectedDevices={selectedDevices}
          onAnswer={answerCall}
          onReject={rejectCall}
          onEnd={endCall}
          onToggleMute={toggleMute}
          onToggleCamera={toggleCamera}
          onToggleScreenShare={toggleScreenShare}
          onSendSoundEffect={sendSoundEffect}
          onSendServerMute={sendServerMute}
          onSwitchDevice={switchDevice}
          onPopout={handlePopout}
        />
      )}

      {isCallMinimized && callState !== 'IDLE' && (
        <CRMChatCallFloating 
          callState={callState}
          callType={callType}
          contact={activeContact || undefined}
          localStream={localStream}
          remoteStreams={remoteStreams}
          isMuted={isMuted}
          isCameraOff={isCameraOff}
          onEnd={endCall}
          onToggleMute={toggleMute}
          onToggleCamera={toggleCamera}
          onMaximize={maximizeCall}
        />
      )}
    </CRMCallContext.Provider>
  );
};
